import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, ArrowDown, MapPin } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/site";
import { MaskLine } from "@/components/site/Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Polished chrome exhaust stack on a semi-truck"
          className="h-full w-full object-cover object-center"
        />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-background/40" />

      <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-8 flex flex-col justify-end pb-16 md:pb-24">
        <MaskLine delay={0.15}>
          <span className="text-[10px] md:text-xs tracking-[0.4em] uppercase text-muted-foreground">
            Semi-Truck Detailing & Aluminum Polishing
          </span>
        </MaskLine>

        <h1 className="font-display uppercase tracking-tighter leading-[0.86] text-[15vw] md:text-[10vw] lg:text-[9rem] mt-4">
          <MaskLine delay={0.25}>Heavy Duty</MaskLine>
          <MaskLine delay={0.4} className="text-outline">Detailing</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-2xl text-sm md:text-base text-muted-foreground"
        >
          At Heavy Duty Detailing, we specialize in premium detailing services for commercial trucks, delivering
          results that reflect the professionalism of every vehicle we touch. We are proud to work closely with
          dealerships throughout Nova Scotia, providing consistent, high-quality detailing that helps prepare trucks
          for sale and presentation. Whether you're a dealership, fleet operator, or owner-operator, our commitment to
          exceptional workmanship, reliability, and attention to detail ensures every truck leaves looking its
          absolute best.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row gap-3"
        >
          <a
            data-testid="hero-call-cta"
            href={CONTACT.phoneHref}
            className="group flex items-center justify-center gap-3 bg-foreground text-background px-8 py-4 text-sm tracking-[0.2em] uppercase border border-foreground hover:bg-transparent hover:text-foreground transition-colors duration-300"
          >
            <Phone size={16} /> Call {CONTACT.phone}
          </a>
          <a
            data-testid="hero-email-cta"
            href={CONTACT.emailHref}
            className="group flex items-center justify-center gap-3 border border-foreground px-8 py-4 text-sm tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors duration-300"
          >
            <Mail size={16} /> Email Us
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-8 flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-muted-foreground"
        >
          <MapPin size={14} /> {CONTACT.region} · Mobile
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ delay: 1.6, duration: 2, repeat: Infinity }}
        className="absolute bottom-6 right-6 md:right-8 z-10 text-muted-foreground"
      >
        <ArrowDown size={18} />
      </motion.div>
    </section>
  );
};
